/**
 * <keetsa-product-grid>
 * Client-side tag filter over a pre-rendered product grid.
 *
 * Wiring:
 *   - Filter pills: <button role="radio" data-filter-tag="...">
 *     Special value "__all__" means "show everything".
 *   - Cards: <li class="keetsa-product-grid__item" data-tags="tag1,tag2,...">
 *     data-tags is a comma-separated, lowercased list.
 *
 * Accessibility:
 *   - role=radiogroup / role=radio with aria-checked already rendered by Liquid.
 *     Pattern: WAI-ARIA radio group (one selected at a time). Selection follows focus.
 *   - Roving tabindex on pills (only the checked one is in the tab order).
 *   - Arrow keys (Left/Right/Up/Down/Home/End) move focus between pills and update selection.
 *   - Enter / Space activate the focused pill.
 *   - Cards are hidden with the `hidden` attribute (removed from a11y tree).
 */
class KeetsaProductGrid extends HTMLElement {
  constructor() {
    super();
    this._onPillClick = this._onPillClick.bind(this);
    this._onPillKeydown = this._onPillKeydown.bind(this);
  }

  connectedCallback() {
    this.pills = Array.from(this.querySelectorAll('.keetsa-product-grid__pill'));
    this.items = Array.from(this.querySelectorAll('.keetsa-product-grid__item'));

    if (this.pills.length === 0 || this.items.length === 0) return;

    this.pills.forEach((pill) => {
      pill.addEventListener('click', this._onPillClick);
      pill.addEventListener('keydown', this._onPillKeydown);
    });

    // Ensure an initial active pill exists — defaults to the first (typically "All").
    const activePill = this.pills.find((p) => p.classList.contains('is-active')) || this.pills[0];
    this._activate(activePill, { focus: false });
  }

  disconnectedCallback() {
    if (!this.pills) return;
    this.pills.forEach((pill) => {
      pill.removeEventListener('click', this._onPillClick);
      pill.removeEventListener('keydown', this._onPillKeydown);
    });
  }

  _onPillClick(event) {
    const pill = event.currentTarget;
    this._activate(pill, { focus: true });
  }

  _onPillKeydown(event) {
    const key = event.key;
    const currentIndex = this.pills.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex = -1;

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % this.pills.length;
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + this.pills.length) % this.pills.length;
    } else if (key === 'Home') {
      nextIndex = 0;
    } else if (key === 'End') {
      nextIndex = this.pills.length - 1;
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      this._activate(event.currentTarget, { focus: true });
      return;
    }

    if (nextIndex !== -1) {
      event.preventDefault();
      this._activate(this.pills[nextIndex], { focus: true });
    }
  }

  _activate(pill, { focus }) {
    if (!pill) return;

    const tag = (pill.dataset.filterTag || '').toLowerCase().trim();

    this.pills.forEach((p) => {
      const isActive = p === pill;
      p.classList.toggle('is-active', isActive);
      p.setAttribute('aria-checked', isActive ? 'true' : 'false');
      p.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this._applyFilter(tag);

    if (focus) {
      // Focus after visibility has been updated.
      pill.focus();
    }
  }

  _applyFilter(tag) {
    const showAll = !tag || tag === '__all__';

    this.items.forEach((item) => {
      if (showAll) {
        item.hidden = false;
        return;
      }

      const raw = (item.dataset.tags || '').toLowerCase();
      if (!raw) {
        item.hidden = true;
        return;
      }

      const itemTags = raw.split(',').map((t) => t.trim()).filter(Boolean);
      item.hidden = !itemTags.includes(tag);
    });
  }
}

if (!customElements.get('keetsa-product-grid')) {
  customElements.define('keetsa-product-grid', KeetsaProductGrid);
}
